import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const apiBase = process.env.NODE_ENV === 'production' 
  ? 'https://proofofprice.com' 
  : 'http://localhost:3001';

interface Quote {
  id: number;
  quote: string;
  source_url?: string;
  prediction_type: string;
  price_prediction?: number;
  price_min?: number;
  price_max?: number;
  end_date: string;
  prediction_made: string;
  created_at: string;
  expert_name: string;
  x_handle: string;
}

const Container = styled.div`
  background: #1a1a1a;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  color: #ffffff;
  margin-bottom: 20px;
  font-size: 20px;
`;

const QuoteCard = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const ExpertInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const ExpertName = styled.h3`
  color: #ffffff;
  margin: 0;
  font-size: 16px;
`;

const PredictionInfo = styled.div`
  color: #888;
  font-size: 14px;
`;

const QuoteText = styled.div`
  background: #1a1a1a;
  border-radius: 6px;
  padding: 15px;
  margin: 15px 0;
  font-size: 14px;
  line-height: 1.5;
  color: #ffffff;
  white-space: pre-wrap;
`;

const EditableQuote = styled.textarea`
  width: 100%;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 15px;
  margin: 15px 0;
  font-size: 14px;
  line-height: 1.5;
  color: #ffffff;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
`;

const EditableSource = styled.input`
  width: 100%;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 12px 15px;
  margin: 10px 0;
  font-size: 14px;
  color: #ffffff;
  font-family: inherit;
  
  &::placeholder {
    color: #666;
  }
  
  &:focus {
    outline: none;
    border-color: #4299e1;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const SourceBadge = styled.div<{ sourceType: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  margin-left: 10px;
  
  ${props => {
    switch (props.sourceType) {
      case 'proofofprice':
        return `
          background: #9c27b0;
          color: white;
        `;
      case 'twitter':
        return `
          background: #1da1f2;
          color: white;
        `;
      case 'youtube':
        return `
          background: #ff0000;
          color: white;
        `;
      case 'podcast':
        return `
          background: #ff9800;
          color: white;
        `;
      case 'external':
        return `
          background: #4caf50;
          color: white;
        `;
      default:
        return `
          background: #666;
          color: white;
        `;
    }
  }}
`;

const Button = styled.button<{ variant?: string }>`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  
  ${props => {
    switch (props.variant) {
      case 'edit':
        return `
          background: #ff9800;
          color: white;
          &:hover { background: #f57c00; }
        `;
      case 'save':
        return `
          background: #4CAF50;
          color: white;
          &:hover { background: #45a049; }
        `;
      case 'cancel':
        return `
          background: #f44336;
          color: white;
          &:hover { background: #da190b; }
        `;
      case 'flag':
        return `
          background: #e91e63;
          color: white;
          &:hover { background: #c2185b; }
        `;
      default:
        return `
          background: #333;
          color: #fff;
          border: 1px solid #555;
          &:hover { background: #555; }
        `;
    }
  }}
`;

const SourceLink = styled.a`
  color: #2196F3;
  text-decoration: none;
  font-size: 14px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: #0f0f0f;
  padding: 15px;
  border-radius: 6px;
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #ffffff;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #888;
  margin-top: 5px;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const QuoteReview: React.FC = () => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [editSource, setEditSource] = useState('');
  const [filter, setFilter] = useState<'all' | 'with_source' | 'no_source' | 'proofofprice' | 'twitter' | 'youtube' | 'podcast'>('all');

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${apiBase}/api/admin/quotes-review`);
      if (response.data.success) {
        setQuotes(response.data.quotes);
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (quote: Quote) => {
    setEditingId(quote.id);
    setEditText(quote.quote);
    setEditSource(quote.source_url || '');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditText('');
    setEditSource('');
  };

  const saveQuote = async (id: number) => {
    try {
      await axios.put(`${apiBase}/api/admin/prediction/${id}/quote`, {
        quote: editText,
        source_url: editSource
      });
      
      // Update local state
      setQuotes(prev => prev.map(q => 
        q.id === id ? { ...q, quote: editText, source_url: editSource } : q
      ));
      
      setEditingId(null);
      setEditText('');
      setEditSource('');
    } catch (error) {
      console.error('Error updating quote/source:', error);
      alert('Error updating quote and source');
    }
  };

  const formatPrice = (prediction: Quote) => {
    if (prediction.prediction_type === 'range') {
      return `$${prediction.price_min?.toLocaleString()} - $${prediction.price_max?.toLocaleString()}`;
    } else if (prediction.prediction_type === 'sentiment') {
      return prediction.price_prediction; // Sentiment text, not a price
    } else if (prediction.prediction_type === 'bet') {
      return `Bet: ${prediction.price_prediction}`; // Bet description
    }
    return `$${prediction.price_prediction?.toLocaleString()}`;
  };

  const getSourceType = (quote: Quote) => {
    if (!quote.source_url || quote.source_url.trim() === '') {
      return 'proofofprice'; // No source URL means user-submitted on proofofprice
    }
    
    const url = quote.source_url.toLowerCase();
    
    if (url.includes('x.com') || url.includes('twitter.com')) {
      return 'twitter';
    }
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      return 'youtube';
    }
    if (url.includes('podcast') || url.includes('fountain.fm') || url.includes('whatbitcoindid.com')) {
      return 'podcast';
    }
    if (url.includes('primal.net') || url.includes('nostr')) {
      return 'nostr';
    }
    
    return 'external';
  };

  const getSourceLabel = (sourceType: string) => {
    switch (sourceType) {
      case 'proofofprice': return '📈 POP';
      case 'twitter': return '🐦 X';
      case 'youtube': return '📺 YT';
      case 'podcast': return '🎙️ POD';
      case 'nostr': return '⚡ NOSTR';
      default: return '🌐 EXT';
    }
  };

  const filteredQuotes = quotes.filter(quote => {
    switch (filter) {
      case 'with_source':
        return quote.source_url && quote.source_url.trim() !== '';
      case 'no_source':
        return !quote.source_url || quote.source_url.trim() === '';
      case 'proofofprice':
      case 'twitter':
      case 'youtube':
      case 'podcast':
        return getSourceType(quote) === filter;
      default:
        return true;
    }
  });

  const stats = {
    total: quotes.length,
    withSource: quotes.filter(q => q.source_url && q.source_url.trim() !== '').length,
    noSource: quotes.filter(q => !q.source_url || q.source_url.trim() === '').length,
    longQuotes: quotes.filter(q => q.quote.length > 500).length
  };

  if (loading) {
    return <Container>Loading quotes...</Container>;
  }

  return (
    <Container>
      <Title>Quote Review & Management</Title>
      
      <Stats>
        <StatCard>
          <StatNumber>{stats.total}</StatNumber>
          <StatLabel>Total Quotes</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.withSource}</StatNumber>
          <StatLabel>With Source</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.noSource}</StatNumber>
          <StatLabel>No Source</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.longQuotes}</StatNumber>
          <StatLabel>Long Quotes (500+ chars)</StatLabel>
        </StatCard>
      </Stats>

      <FilterGroup>
        <Button 
          variant={filter === 'all' ? 'edit' : undefined}
          onClick={() => setFilter('all')}
        >
          All ({stats.total})
        </Button>
        <Button 
          variant={filter === 'with_source' ? 'edit' : undefined}
          onClick={() => setFilter('with_source')}
        >
          With Source ({stats.withSource})
        </Button>
        <Button 
          variant={filter === 'no_source' ? 'edit' : undefined}
          onClick={() => setFilter('no_source')}
        >
          No Source ({stats.noSource})
        </Button>
        <Button 
          variant={filter === 'proofofprice' ? 'edit' : undefined}
          onClick={() => setFilter('proofofprice')}
        >
          📈 ProofOfPrice
        </Button>
        <Button 
          variant={filter === 'twitter' ? 'edit' : undefined}
          onClick={() => setFilter('twitter')}
        >
          🐦 X/Twitter
        </Button>
        <Button 
          variant={filter === 'youtube' ? 'edit' : undefined}
          onClick={() => setFilter('youtube')}
        >
          📺 YouTube
        </Button>
        <Button 
          variant={filter === 'podcast' ? 'edit' : undefined}
          onClick={() => setFilter('podcast')}
        >
          🎙️ Podcast
        </Button>
      </FilterGroup>

      {filteredQuotes.map((quote) => (
        <QuoteCard key={quote.id}>
          <ExpertInfo>
            <div>
              <ExpertName>
                {quote.expert_name}
                <SourceBadge sourceType={getSourceType(quote)}>
                  {getSourceLabel(getSourceType(quote))}
                </SourceBadge>
              </ExpertName>
              <PredictionInfo>
                {formatPrice(quote)} by {new Date(quote.end_date).toLocaleDateString()}
                {quote.source_url && (
                  <>
                    {' • '}
                    <SourceLink href={quote.source_url} target="_blank" rel="noopener noreferrer">
                      View Source
                    </SourceLink>
                  </>
                )}
              </PredictionInfo>
            </div>
            <div>
              <PredictionInfo>
                ID: {quote.id} • {quote.quote.length} chars
              </PredictionInfo>
            </div>
          </ExpertInfo>

          {editingId === quote.id ? (
            <>
              <div style={{ marginBottom: '10px', color: '#ccc', fontSize: '14px' }}>
                <strong>Quote:</strong>
              </div>
              <EditableQuote
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder="Edit quote..."
              />
              <div style={{ marginBottom: '5px', color: '#ccc', fontSize: '14px' }}>
                <strong>Source URL:</strong>
              </div>
              <EditableSource
                value={editSource}
                onChange={(e) => setEditSource(e.target.value)}
                placeholder="https://twitter.com/username/status/1234567890 or https://youtube.com/..."
                type="url"
              />
              <ButtonGroup>
                <Button variant="save" onClick={() => saveQuote(quote.id)}>
                  Save Quote & Source
                </Button>
                <Button variant="cancel" onClick={cancelEditing}>
                  Cancel
                </Button>
              </ButtonGroup>
            </>
          ) : (
            <>
              <QuoteText>{quote.quote}</QuoteText>
              <ButtonGroup>
                <Button variant="edit" onClick={() => startEditing(quote)}>
                  Edit Quote & Source
                </Button>
                {!quote.source_url && (
                  <Button variant="flag">
                    No Source ⚠️
                  </Button>
                )}
                {quote.quote.length > 500 && (
                  <Button variant="flag">
                    Long Quote ({quote.quote.length} chars)
                  </Button>
                )}
              </ButtonGroup>
            </>
          )}
        </QuoteCard>
      ))}
    </Container>
  );
};

export default QuoteReview;